import { useStaticCallback } from '@sparemin/blockhead';
import { useCallback, useEffect, useRef, useState } from 'react';
import { EddyElement } from 'blocks/EddyEmbed';
import { millisToSec } from 'utils/time';
import { useClipSelect } from '../../ClipSelectContext';
import { ClipSelectPreviewPlayerInstance } from '../../ClipSelectVideoPlayer/ClipSelectPreviewPlayer';
import {
  isPreviewLoadingSelector,
  previewWidgetIdSelector,
} from '../selectors';
import { OnPlaybackChange } from './types';
import useCTAFlow from './useCTAFlow';
import usePlaybackController from './usePlaybackController';
import useReadyState from './useReadyState';
import useReprocessFlow from './useReprocessFlow';
import useSeekSync from './useSeekSync';

export type UseClipSelectDetailCardControllerProps = {
  suggestionId: number;
  onPlaybackChange: OnPlaybackChange;
};

export default function useClipSelectDetailCardController({
  suggestionId,
  onPlaybackChange,
}: UseClipSelectDetailCardControllerProps) {
  // -- context values and local state
  const {
    isGeneratingPreview,
    clipsPageInfo: { aspectRatioName },
    suggestion,
    onUpdateSuggestion,
  } = useClipSelect({
    clipId: suggestionId,
  });

  const [lastPlaybackPosSec, setLastPlaybackPosSec] = useState<
    number | undefined
  >();

  const eddyRef = useRef<EddyElement>();
  const previewRef = useRef<ClipSelectPreviewPlayerInstance>();

  const previewWidgetId = previewWidgetIdSelector(suggestion);

  // -- ready state
  const {
    readyState,
    handleEddyReady,
    handlePreviewReady: handlePreviewReadyState,
    handlePreviewDestroy,
  } = useReadyState();

  const isPreviewLoading = isPreviewLoadingSelector({
    isGeneratingPreview,
    isInitialized: readyState.isInitialized,
    isPreviewReady: readyState.isPreviewReady,
    previewWidgetId,
  });

  // -- seek sync
  const { handleEddySeek, handlePreviewSeek } = useSeekSync({
    suggestionId,
    eddySeek: useCallback((millis: number) => {
      eddyRef.current?.seek(millis);
      setLastPlaybackPosSec(millisToSec(millis));
    }, []),
    previewSeek: useCallback((sec: number) => {
      previewRef.current?.setCurrentTime(sec);
      setLastPlaybackPosSec(sec);
    }, []),
  });

  // -- reprocessing flow
  const {
    isPreviewDirty,
    isPendingReprocess,
    reprocess,
    reprocessNow,
    cancelReprocess,
  } = useReprocessFlow({
    suggestionId,
  });

  // -- playback controller
  const {
    clearPendingPlayback,
    isPlaying,
    handlePlaybackChange,
    resumePendingPlayback,
  } = usePlaybackController({
    isPreviewLoading,
    isPreviewDirty,
    reprocessNow,
    onPlaybackChange,
  });

  // -- CTA / export / overlay flow
  const {
    ctaIntent,
    showLoadingOverlay,
    handleCloseOverlay,
    handleSubmit: handleCTASubmit,
    handleOpenAdvancedEditor: handleCTAOpenAdvancedEditor,
  } = useCTAFlow({
    cancelReprocess,
    isPendingReprocess,
    isPreviewDirty,
    reprocessNow,
    suggestionId,
    onExportStart: () => {
      handlePlaybackChange(false);
    },
  });

  // -- wiring for some handlers that use multiple hooks, as well as effects to
  // clean up state when the component mounts/unmounts

  const handlePreviewReady = useCallback(() => {
    handlePreviewReadyState();
    resumePendingPlayback();
  }, [handlePreviewReadyState, resumePendingPlayback]);

  const handleOpenAdvancedEditor = useCallback(async () => {
    clearPendingPlayback();
    handleCTAOpenAdvancedEditor();
  }, [clearPendingPlayback, handleCTAOpenAdvancedEditor]);

  const handleSubmit = useCallback(() => {
    clearPendingPlayback();
    handleCTASubmit();
  }, [clearPendingPlayback, handleCTASubmit]);

  const staticOnUpdateSuggestion = useStaticCallback(onUpdateSuggestion);

  useEffect(
    () => () => {
      // when the detail view unmounts, cancel all pending chnages and fetch the
      // suggestion one last time, so that state is up-to-date in case the user
      // exports from the "browse" view
      // debouncedReprocess.cancel();
      staticOnUpdateSuggestion?.();
    },
    [staticOnUpdateSuggestion],
  );

  return {
    aspectRatioName,
    eddyRef,
    ctaIntent,
    handleCloseLoadingOverlay: handleCloseOverlay,
    isEddyReady: readyState.isEddyReady,
    isInitialized: readyState.isInitialized,
    isPlaying,
    isPreviewLoading,
    isPreviewReady: readyState.isPreviewReady,
    lastPlaybackPosSec,
    handleEddyReady,
    handleEddySeek,
    handlePlaybackChange,
    handlePreviewDestroy,
    handlePreviewReady,
    handlePreviewSeek,
    handleOpenAdvancedEditor,
    handleSubmit,
    handleTranscriptUpdate: reprocess,
    previewRef,
    previewWidgetId,
    showLoadingOverlay,
  };
}
